import React, { useEffect, useMemo, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Check, Search } from 'lucide-react';
import { useLoopingClock } from '@/hooks/useLoopingClock';

/**
 * Decorative manuscript demo for the signed-out /write landing.
 *
 * One small page that assembles itself in front of the visitor: the agent
 * finds and cites the literature, writes the section, sets the mathematics,
 * draws the figure, tightens a passage, then fixes the LaTeX and compiles.
 * Every scene adds something to the same page, so the resting frame is a
 * page that looks like a real paper — the productivity argument is the speed
 * at which the results appear, one after another.
 *
 * The page frame, title, byline and opening line are furniture: they are on
 * screen from the first frame, so the card is never an empty rectangle, and
 * the first scene is visibly working within a heartbeat.
 *
 * Purely visual: no PDF, no network, no real manuscript. All placeholder
 * content is field-neutral filler, never a fake published paper and never
 * invented metrics. Reduced motion and the paused state (visitor is engaged
 * with the composer) render the finished, fully assembled page with no
 * timers running.
 */

export type ProofScene = 'literature' | 'write' | 'math' | 'figure' | 'revise' | 'repair';

interface Scene {
  id: ProofScene;
  /** The short thing a researcher would type to ask for this scene. */
  ask: string;
  /** Shown while the scene is running. */
  running: string;
  /** Shown once the scene's result is on the page. */
  caption: string;
  /** Seconds from cycle start at which the scene begins. */
  start: number;
  /** Seconds from cycle start at which the result is visible. */
  resultAt: number;
}

/** A calm request rhythm: type, rest long enough to read, then hand off. */
const ASK_TYPE = 0.7;
const ASK_HOLD = 0.85;
const ASK_EXIT = 0.95;
const ASK_LEAD = ASK_TYPE + ASK_HOLD;


// Every scene shows visible progress from its first frame and finishes within
// about four seconds, so ten seconds of attention buys two finished results.
const SCENES: Scene[] = [
  {
    id: 'literature',
    ask: 'find the key papers and cite them',
    running: 'Searching the literature',
    caption: 'Found the sources and cited them',
    start: 1.55,
    resultAt: 4.15,
  },
  {
    id: 'write',
    ask: 'write the related work section',
    running: 'Writing the section',
    caption: 'Wrote the section',
    start: 6.15,
    resultAt: 9.15,
  },
  {
    id: 'math',
    ask: 'add the loss gradient, check it',
    running: 'Typesetting the derivation',
    caption: 'Typeset and checked the derivation',
    start: 10.75,
    resultAt: 13.95,
  },
  {
    id: 'figure',
    ask: 'draw the pipeline as a figure',
    running: 'Drawing the figure in TikZ',
    caption: 'Drew the figure in TikZ',
    start: 15.35,
    resultAt: 17.75,
  },
  {
    id: 'revise',
    ask: 'this sentence is too wordy',
    running: 'Tightening the abstract',
    caption: 'Tightened the abstract',
    start: 19.95,
    resultAt: 21.75,
  },
  {
    id: 'repair',
    ask: 'the compile is failing',
    running: 'Fixing the LaTeX',
    caption: 'Fixed the LaTeX and compiled',
    start: 24.55,
    resultAt: 26.95,
  },
];

const SCENES_END = 27.5;
const REST_SECONDS = 4;
const CYCLE_SECONDS = SCENES_END + REST_SECONDS;
/** The assembled page fades out instead of snapping back to blank. */
const FADE_OUT_AT = CYCLE_SECONDS - 0.8;
const TICK_MS = 100;

const sceneTime = (id: ProofScene) => {
  const scene = SCENES.find((candidate) => candidate.id === id);
  return scene?.start ?? 0;
};

const LITERATURE_AT = sceneTime('literature');
const WRITE_AT = sceneTime('write');
const MATH_AT = sceneTime('math');
const FIGURE_AT = sceneTime('figure');
const REVISE_AT = sceneTime('revise');
const REPAIR_AT = sceneTime('repair');

export interface AskAnimationState {
  scene: Scene | null;
  text: string;
  typing: boolean;
  leaving: boolean;
}

/** Pure clock projection so the full type, hold, and handoff rhythm is testable. */
export const getAskAnimationState = (
  clock: number,
  reducedMotion = false,
): AskAnimationState => {
  if (reducedMotion) return { scene: null, text: '', typing: false, leaving: false };

  const scene =
    SCENES.find(
      (candidate) =>
        clock >= candidate.start - ASK_LEAD && clock < candidate.start + ASK_EXIT,
    ) ?? null;

  if (!scene) return { scene: null, text: '', typing: false, leaving: false };

  const requestStart = scene.start - ASK_LEAD;
  const progress = Math.max(0, Math.min(1, (clock - requestStart) / ASK_TYPE));
  return {
    scene,
    text: scene.ask.slice(0, Math.max(1, Math.ceil(progress * scene.ask.length))),
    typing: progress < 1,
    leaving: clock >= scene.start,
  };
};

const CANDIDATES = [
  'Learned coordination in distributed systems',
  'Protocols versus policies: a comparison',
  'Emergent coordination in multi-agent settings',
];

const PARAGRAPH =
  'Recent work increasingly frames coordination as a learned behaviour rather than a fixed protocol, with results reported across simulation and small testbeds';

const TIGHTENED = 'Coordination is learned, not configured.';

interface ManuscriptProofProps {
  /** `card` is the desktop page; `strip` is the one-line mobile summary. */
  variant?: 'card' | 'strip';
  /** A starter chip being hovered/focused: jump straight to that scene. */
  intent?: ProofScene | null;
  /** Visitor is typing or focused: settle on the finished page, no motion. */
  paused?: boolean;
  /** Change this value to restart the cycle from the first scene. */
  replayToken?: number | string;
}

const ManuscriptProof: React.FC<ManuscriptProofProps> = ({
  variant = 'card',
  intent = null,
  paused = false,
  replayToken = 0,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const still = Boolean(prefersReducedMotion) || paused;
  const { clock: t, setClock: setT } = useLoopingClock(CYCLE_SECONDS, still, TICK_MS);

  // Hovering a suggestion jumps the page to the scene that answers it.
  useEffect(() => {
    if (still || !intent) return;
    const scene = SCENES.find((s) => s.id === intent);
    if (scene) setT(scene.start);
  }, [still, intent]);

  // "See how it works" replay: restart the cycle from the first scene. When
  // the composer is active the proof is settled, so the replay takes effect
  // on the next idle moment instead of yanking motion back under the cursor.
  const lastReplay = React.useRef(replayToken);
  const pendingReplay = React.useRef(false);
  useEffect(() => {
    if (replayToken !== lastReplay.current) {
      lastReplay.current = replayToken;
      pendingReplay.current = true;
    }
    if (!still && pendingReplay.current) {
      pendingReplay.current = false;
      setT(0);
    }
  }, [replayToken, still]);

  // Still state: everything at its finished value.
  const clock = still ? SCENES_END : t;
  const after = (time: number) => clock >= time;

  const activeScene = useMemo(() => {
    if (still) return SCENES[SCENES.length - 1];
    return [...SCENES].reverse().find((s) => clock >= s.start) ?? SCENES[0];
  }, [still, clock]);

  const activeLabel = still || clock >= activeScene.resultAt ? activeScene.caption : activeScene.running;
  const done = still || clock >= activeScene.resultAt;

  if (variant === 'strip') {
    // One line under the prompt: current job and its result, cycling.
    return (
      <div
        className="pointer-events-none select-none text-center"
        aria-hidden="true"
        data-testid="manuscript-proof-strip"
      >
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70">
          {done ? (
            <Check className="h-3 w-3 text-primary/70" />
          ) : (
            <Search className="h-3 w-3 animate-pulse text-muted-foreground/60" />
          )}
          <span key={activeLabel} className="animate-fade-in">
            {activeLabel}
          </span>
        </span>
      </div>
    );
  }

  // Typing progress for the paragraph (write scene).
  const typedChars = still
    ? PARAGRAPH.length
    : Math.max(
        0,
        Math.min(
          PARAGRAPH.length,
          Math.floor(((clock - (WRITE_AT + 0.4)) / 2.6) * PARAGRAPH.length),
        ),
      );

  const fading = !still && clock >= FADE_OUT_AT;

  // The ask that produced the scene about to run: typed out in the quiet gap
  // between two scenes, then it drifts away as the work lands. Never in the
  // settled state, where a floating user message next to a real input would
  // only confuse.
  // The request is complete and still for ASK_HOLD before its slow handoff.
  const askState = getAskAnimationState(clock, still);
  const activeAsk = askState.scene;

  return (
    <div className="pointer-events-none select-none" aria-hidden="true" data-testid="manuscript-proof">
      <div className="rounded-2xl border border-border/40 bg-card/60 p-4 shadow-[0_40px_100px_-40px_hsl(var(--foreground)/0.25)] backdrop-blur-[1px] sm:p-5">
        <div className="mb-3 flex h-6 items-center justify-between">
          <span className="shrink-0 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground/70">
            main.pdf
          </span>
          <span className="relative ml-3 block h-6 min-w-0 flex-1">
            {activeAsk && (
              <span className="absolute inset-y-0 right-0 flex max-w-[72%] items-center">
                <span
                  key={activeAsk.id}
                  data-testid="manuscript-proof-ask"
                  data-phase={askState.typing ? 'typing' : askState.leaving ? 'leaving' : 'holding'}
                  className={`inline-flex min-w-0 items-center gap-1 truncate rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-medium text-primary/80 transition-[opacity,transform] duration-[950ms] ease-in-out ${
                    askState.leaving
                      ? 'translate-x-2 -translate-y-1.5 opacity-0'
                      : 'translate-x-0 translate-y-0 opacity-100'
                  }`}
                >
                  <span className="truncate">{askState.text}</span>
                  {askState.typing && (
                    <span className="inline-block h-2.5 w-px shrink-0 animate-pulse bg-primary/70" />
                  )}
                </span>
              </span>
            )}
            {after(26.95) && !activeAsk && (
              <span className="absolute inset-y-0 right-0 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary/80">
                <Check className="h-2.5 w-2.5" />
                compiled
              </span>
            )}
          </span>
        </div>

        <div
          className="h-[30rem] overflow-hidden rounded-xl border border-border/40 bg-background/70 px-5 py-6 text-left sm:px-7"
          data-testid="manuscript-proof-canvas"
        >
          <div
            className={`h-full transition-opacity duration-700 ${fading ? 'opacity-0' : 'opacity-100'}`}
          >
            {/* Page furniture: on screen from the first frame, never blank */}
            <div className="h-[3.5rem] text-center">
              <div className="font-serif text-[13px] font-semibold text-foreground/75">
                Learned coordination in distributed systems
              </div>
              <div className="mx-auto mt-1.5 h-px w-16 bg-border/70" />
              <div className="mt-1.5 font-serif text-[9px] italic text-muted-foreground/60">
                Draft · working paper
              </div>
            </div>

            <p className="h-[2.5rem] font-serif text-[11px] leading-relaxed text-foreground/55">
              Coordination in distributed systems has been studied for decades{' '}
              {after(LITERATURE_AT + 2.2) ? (
                <span className="rounded bg-primary/10 px-1 font-mono text-[9px] text-primary/80 transition-opacity duration-500">
                  [12]
                </span>
              ) : (
                <span className="rounded bg-muted-foreground/10 px-1 font-mono text-[9px] text-muted-foreground/60">
                  [?]
                </span>
              )}
            </p>

            {/* Literature and prose share one reserved region, so the paper never reflows. */}
            <div className="relative h-[5.5rem]">
              <div
                className={`absolute inset-0 rounded-lg border border-border/40 bg-muted/30 px-3 py-2 transition-opacity duration-500 ${
                  after(LITERATURE_AT + 2.2) ? 'opacity-0' : 'opacity-100'
                }`}
              >
                <div className="mb-1.5 inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  <Search className="h-2.5 w-2.5 animate-pulse" />
                  searching the literature
                </div>
                {CANDIDATES.map((c, i) =>
                  after(LITERATURE_AT + 0.3 + i * 0.5) ? (
                    <div
                      key={c}
                      className="truncate font-serif text-[9px] leading-relaxed text-muted-foreground/60 transition-opacity duration-500"
                    >
                      {c}
                    </div>
                  ) : null,
                )}
              </div>
              <div className="absolute inset-0">
                <div className="mb-1 h-4 font-serif text-sm font-semibold text-foreground/80">
                  {after(WRITE_AT + 0.2) && (
                    <span className="transition-opacity duration-500">Related work</span>
                  )}
                </div>
                <p className="h-[3.75rem] font-serif text-[11px] leading-relaxed text-foreground/55">
                  {PARAGRAPH.slice(0, typedChars)}
                  {!still && clock >= WRITE_AT + 0.4 && clock < SCENES[1].resultAt && (
                    <span className="ml-0.5 inline-block h-3 w-px animate-pulse bg-foreground/50 align-middle" />
                  )}
                </p>
              </div>
            </div>

            {/* Math scene: a LaTeX line collapses into a typeset equation */}
            <div className="relative h-[3rem]">
              {!after(MATH_AT + 2.4) && after(MATH_AT + 0.4) && (
                <p className="absolute inset-x-0 top-1 font-mono text-[10px] text-muted-foreground/60 transition-opacity duration-500">
                  \nabla_&#952; J(&#952;) = \sum_i &#8706;L_i / &#8706;&#952;
                </p>
              )}
              {after(MATH_AT + 2.4) && (
                <p className="absolute inset-x-0 top-1 text-center font-serif text-sm italic text-foreground/70 transition-opacity duration-500">
                  &#8711;<sub>&#952;</sub> J(&#952;) = &#8721;<sub>i</sub> &#8706;L<sub>i</sub> / &#8706;&#952;
                  {after(SCENES[2].resultAt) && (
                    <span className="ml-2 inline-flex items-center gap-0.5 align-middle text-[9px] not-italic text-primary/70">
                      <Check className="h-2.5 w-2.5" />
                      checked
                    </span>
                  )}
                </p>
              )}
            </div>

            {/* Figure scene: TikZ lines turn into an actual small diagram */}
            <div className="relative h-[4.25rem]">
              {!after(SCENES[3].resultAt) && after(FIGURE_AT + 0.4) && (
                <p className="absolute inset-x-0 top-1 font-mono text-[10px] leading-relaxed text-muted-foreground/60 transition-opacity duration-500">
                  \node (a) &#123;sense&#125;; \node (b) &#123;decide&#125;;
                  <br />
                  \draw[-&gt;] (a) -- (b);
                </p>
              )}
              {after(SCENES[3].resultAt) && (
                <div className="absolute inset-x-0 top-0 flex flex-col items-center transition-opacity duration-500">
                  <svg width="150" height="34" viewBox="0 0 150 34" className="text-foreground/50">
                    <circle cx="20" cy="17" r="8" fill="none" stroke="currentColor" strokeWidth="1" />
                    <circle cx="75" cy="17" r="8" fill="none" stroke="currentColor" strokeWidth="1" />
                    <circle cx="130" cy="17" r="8" fill="none" stroke="currentColor" strokeWidth="1" />
                    <line x1="28" y1="17" x2="65" y2="17" stroke="currentColor" strokeWidth="1" />
                    <line x1="83" y1="17" x2="120" y2="17" stroke="currentColor" strokeWidth="1" />
                  </svg>
                  <span className="mt-1 font-serif text-[9px] text-muted-foreground/60">
                    Figure 1: sense, decide, act.
                  </span>
                </div>
              )}
            </div>

            {/* Revise scene: a sentence tightens */}
            <div className="h-[3rem]">
              {after(REVISE_AT + 0.4) && (
                <p className="font-serif text-[11px] leading-relaxed transition-opacity duration-500">
                <span className="text-muted-foreground/50 line-through decoration-destructive/50">
                  It can be observed that the behaviour of coordination is learned.
                </span>{' '}
                  {after(SCENES[4].resultAt) && (
                    <span className="text-foreground/60 transition-opacity duration-500">{TIGHTENED}</span>
                  )}
                </p>
              )}
            </div>

            {/* Literature scene: the reference lands in the bibliography */}
            <div className="h-[4.25rem] border-t border-border/40 pt-3">
              {after(LITERATURE_AT + 2.4) && (
                <div className="mb-1.5 font-serif text-[10px] font-semibold text-foreground/60">
                  References
                </div>
              )}
              {after(SCENES[0].resultAt) && (
                <p className="font-serif text-[9px] leading-relaxed text-muted-foreground/60 transition-opacity duration-500">
                  [12] A. Researcher et al., &ldquo;Learned coordination in distributed
                  systems,&rdquo; <em>Journal of Placeholder Studies</em>, 2026.
                </p>
              )}
            </div>

            {/* Repair scene: a red LaTeX error is replaced by a green fix */}
            <div className="relative h-6 pt-2">
              {after(REPAIR_AT + 0.4) && !after(REPAIR_AT + 2) && (
                <p className="absolute inset-x-0 top-2 font-mono text-[9px] text-destructive/80 transition-opacity duration-500">
                  ! Misplaced alignment tab character &amp;.
                </p>
              )}
              {after(REPAIR_AT + 2) && (
                <p className="absolute inset-x-0 top-2 inline-flex items-center gap-1 font-mono text-[9px] text-primary/80 transition-opacity duration-500">
                  <Check className="h-2.5 w-2.5" />
                  fixed, recompiling&hellip;
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* The scene is named as the work it replaces, in one quiet line */}
      <p className="mt-3 h-4 text-center text-xs text-muted-foreground/70">
        <span key={activeLabel} className="inline-flex animate-fade-in items-center gap-1.5">
          {done ? (
            <Check className="h-3 w-3 text-primary/70" />
          ) : (
            <Search className="h-3 w-3 animate-pulse text-muted-foreground/60" />
          )}
          {activeLabel}
        </span>
      </p>
    </div>
  );
};

export default ManuscriptProof;
